import {
    ATTACHABLE_TYPES,
    imageFilesFrom,
    insertAt,
    uploadImage,
    uploadPlaceholder,
} from '../../lib/attachments.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html, nothing } from '../../lib/lit.js';

/** Distinguishes concurrent uploads' placeholders. Not persisted anywhere. */
let placeholderSequence = 0;

/**
 * <d3-markdown-input> — a textarea you can paste, drag or pick an image into.
 * A port of the community portal's `community-markdown-input`, so a screenshot
 * in a poll behaves here the way it does there.
 *
 * It owns the label as well as the field: with shadow DOM a `<label for>` in
 * the parent cannot reach a textarea in here.
 *
 * Emits `value-changed` with `{ value }` on every edit, typed or pasted, and
 * `uploading-changed` with `{ uploading }` as uploads start and finish. The
 * parent stays the owner of the text; this only reports.
 */
class MarkdownInput extends BaseComponent {
    static properties = {
        value: { type: String },
        label: { type: String },
        placeholder: { type: String },
        rows: { type: Number },
        maxlength: { type: Number },
        disabled: { type: Boolean },
        inputId: { type: String, attribute: 'input-id' },
        _uploading: { state: true },
        _armed: { state: true },
        _over: { state: true },
        _error: { state: true },
    };

    // Two drag states: "can I drop a picture here at all?" the moment a drag
    // starts anywhere on the page (armed), and "will it land HERE if I let go?"
    // once it is over the field (over).
    static styles = css`
        .drop-zone {
            position: relative;
        }
        .drop-zone textarea {
            transition:
                border-color 0.1s ease,
                box-shadow 0.1s ease;
        }
        .drop-zone.armed textarea {
            border: 1px dashed var(--bs-primary, #0d6efd);
        }
        .drop-zone.over textarea {
            border: 1px dashed var(--bs-primary, #0d6efd);
            box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        .drop-overlay {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            border-radius: var(--bs-border-radius, 0.375rem);
            background: rgba(13, 110, 253, 0.08);
            color: var(--bs-primary, #0d6efd);
            font-weight: 600;
            /* The overlay sits ON the textarea, so it must not intercept the
               drag events the zone underneath is listening for. */
            pointer-events: none;
        }
        textarea {
            font-family: ui-monospace, monospace;
            font-size: 0.9rem;
        }
    `;

    constructor() {
        super();
        this.value = '';
        this.label = '';
        this.placeholder = '';
        this.rows = 5;
        this.maxlength = 20000;
        this.disabled = false;
        this.inputId = 'markdown-input';
        this._uploading = 0;
        this._armed = false;
        this._over = false;
        this._error = '';
        this._disarmTimer = 0;
    }

    connectedCallback() {
        super.connectedCallback();
        // Watched on the document, not on the field: the point is to light the
        // field up the moment a file is dragged ANYWHERE over the page.
        document.addEventListener('dragover', this._onDocumentDrag);
        document.addEventListener('drop', this._disarm);
        document.addEventListener('dragend', this._disarm);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('dragover', this._onDocumentDrag);
        document.removeEventListener('drop', this._disarm);
        document.removeEventListener('dragend', this._disarm);
        clearTimeout(this._disarmTimer);
    }

    /**
     * Arm while a file drag is in progress, and disarm shortly after it stops.
     * A drag can end in ways that fire nothing here (dropped elsewhere,
     * cancelled, dragged out of the window), and `dragover` repeats every frame
     * while one is live — so "still arriving" is the signal.
     * @param {DragEvent} event
     */
    _onDocumentDrag = (event) => {
        if (this.disabled || !event.dataTransfer?.types?.includes('Files')) return;
        this._armed = true;
        clearTimeout(this._disarmTimer);
        this._disarmTimer = setTimeout(this._disarm, 150);
    };

    _disarm = () => {
        clearTimeout(this._disarmTimer);
        this._armed = false;
        this._over = false;
    };

    /** @returns {HTMLTextAreaElement} */
    get _textarea() {
        return this.renderRoot.querySelector('textarea');
    }

    /**
     * Report the text as it now stands. Reads the live element rather than
     * `this.value`, because an upload finishing has to act on what the person
     * has typed SINCE it started — and the property only catches up a render
     * after the parent hears about it.
     */
    _emit() {
        this.value = this._textarea.value;
        this.dispatchEvent(
            new CustomEvent('value-changed', {
                detail: { value: this.value },
                bubbles: true,
                composed: true,
            }),
        );
    }

    /**
     * Write `next` into the field and report it, putting the caret at `caret`.
     * @param {string} next
     * @param {number} caret
     */
    _setValue(next, caret) {
        const textarea = this._textarea;
        textarea.value = next;
        if (typeof caret === 'number') textarea.setSelectionRange(caret, caret);
        this._emit();
    }

    /**
     * Take the images out of a paste or drop and upload them. Only calls
     * preventDefault when there is actually an image: pasting text must keep
     * working, and so must dropping one.
     * @param {ClipboardEvent|DragEvent} event
     */
    async _handleImages(event) {
        if (this.disabled) return;
        const files = imageFilesFrom(event);
        if (files.length === 0) return;

        event.preventDefault();
        this._disarm();
        this._error = '';
        await Promise.all(files.map((file) => this._upload(file)));
    }

    /**
     * Upload one image, holding its place in the text meanwhile.
     * @param {File} file
     */
    async _upload(file) {
        const name = file.name || 'screenshot.png';
        const marker = uploadPlaceholder(String(++placeholderSequence), name);

        const textarea = this._textarea;
        const { value, caret } = insertAt(
            textarea.value,
            textarea.selectionStart,
            textarea.selectionEnd,
            marker,
        );
        this._setValue(value, caret);
        this._setUploading(this._uploading + 1);

        try {
            const markdown = await uploadImage(file);
            this._replaceMarker(marker, markdown);
        } catch (err) {
            // Take the placeholder back out: leaving it would save as literal
            // text, and it says "uploading" about something that never will.
            this._replaceMarker(marker, '');
            this._error = /** @type {any} */ (err).message || String(err);
        } finally {
            this._setUploading(this._uploading - 1);
        }
    }

    /**
     * Track in-flight uploads and tell the parent, which owns the save button:
     * saving mid-upload would store the placeholder as literal text.
     * @param {number} count
     */
    _setUploading(count) {
        const was = this._uploading > 0;
        this._uploading = count;
        const now = this._uploading > 0;
        if (was === now) return;
        this.dispatchEvent(
            new CustomEvent('uploading-changed', {
                detail: { uploading: now },
                bubbles: true,
                composed: true,
            }),
        );
    }

    /**
     * Swap one placeholder for its result, leaving the caret alone unless it sat
     * after the edit — in which case it shifts by the change in length.
     * @param {string} marker
     * @param {string} replacement
     */
    _replaceMarker(marker, replacement) {
        const textarea = this._textarea;
        const index = textarea.value.indexOf(marker);
        // Gone: the person deleted the placeholder. Their edit wins.
        if (index < 0) return;

        // An emptied placeholder takes its own trailing newline with it.
        const width =
            replacement === '' && textarea.value[index + marker.length] === '\n'
                ? marker.length + 1
                : marker.length;
        const next =
            textarea.value.slice(0, index) + replacement + textarea.value.slice(index + width);

        const caret = textarea.selectionStart;
        const shifted = caret > index ? caret + (replacement.length - width) : caret;
        this._setValue(next, Math.max(0, Math.min(shifted, next.length)));
    }

    /** @param {Event} event */
    async _choose(event) {
        const input = /** @type {HTMLInputElement} */ (event.target);
        const files = Array.from(input.files ?? []);
        // Let the same file be picked twice in a row — the input keeps its
        // value otherwise, and the second pick fires no change event.
        input.value = '';
        this._error = '';
        await Promise.all(files.map((file) => this._upload(file)));
    }

    render() {
        return html`
            ${
                this.label
                    ? html`<label class="form-label" for=${this.inputId}>${this.label}</label>`
                    : nothing
            }
            <div
                class="drop-zone ${this._armed ? 'armed' : ''} ${this._over ? 'over' : ''}"
                @drop=${(e) => this._handleImages(e)}
                @dragover=${(e) => {
                    if (this.disabled || !e.dataTransfer?.types?.includes('Files')) return;
                    // Without this the browser refuses the drop and opens the
                    // image in the tab instead.
                    e.preventDefault();
                    this._over = true;
                }}
                @dragleave=${(e) => {
                    // Moving onto the textarea inside the zone still fires
                    // dragleave on the zone; only a target outside it counts.
                    if (!e.currentTarget.contains(e.relatedTarget)) this._over = false;
                }}
            >
                <textarea
                    id=${this.inputId}
                    class="form-control"
                    rows=${this.rows}
                    maxlength=${this.maxlength}
                    placeholder=${this.placeholder}
                    .value=${this.value}
                    ?disabled=${this.disabled}
                    @input=${() => this._emit()}
                    @paste=${(e) => this._handleImages(e)}
                ></textarea>
                ${
                    this._over
                        ? html`<div class="drop-overlay">
                              <i class="bi bi-image"></i>Drop to attach
                          </div>`
                        : nothing
                }
            </div>

            <div class="form-text d-flex align-items-center gap-2 flex-wrap">
                <span>
                    Markdown — **bold**, lists, \`code\`, links.
                    ${
                        this.disabled
                            ? nothing
                            : html`Paste or drag an image to attach it, or
                                  <label class="text-decoration-underline" style="cursor: pointer">
                                      choose a file
                                      <input
                                          type="file"
                                          class="visually-hidden"
                                          accept=${ATTACHABLE_TYPES.join(',')}
                                          multiple
                                          @change=${(e) => this._choose(e)}
                                      />
                                  </label>.`
                    }
                </span>
                ${
                    this._uploading > 0
                        ? html`<span class="text-primary">
                              <span
                                  class="spinner-border spinner-border-sm me-1"
                                  role="status"
                              ></span>
                              Uploading…
                          </span>`
                        : nothing
                }
            </div>

            ${
                this._error
                    ? html`<div class="alert alert-warning py-1 px-2 mt-2 mb-0 small" role="alert">
                          ${this._error}
                      </div>`
                    : nothing
            }
        `;
    }
}

customElements.define('d3-markdown-input', MarkdownInput);
